import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { McValidators } from '@portal-core/forms/services/mc-validators';
import { DeleteConfirmDialogData } from '@portal-core/general/components/form/form.component';
import { ConnectionType } from '@portal-core/publications/enums/publication-connection-type.enum';
import { ConnectorProperties } from '@portal-core/publications/models/connector-properties.model';
import { ConnectorSecrets } from '@portal-core/publications/models/connector-secrets.model';
import { ConnectorSettings } from '@portal-core/publications/models/connector-settings.model';
import { ConnectorUpdate } from '@portal-core/publications/models/connector-update.model';
import { Connector } from '@portal-core/publications/models/connector.model';
import { ConnectorService } from '@portal-core/publications/services/connector.service';

export interface SyndicateForm {
  syndicateName: FormControl<string>;
  elevateUrl: FormControl<string>;
  syndicateUrl: FormControl<string>;
  clientId: FormControl<string>;
  apiKey: FormControl<string>;
}

@Component({
  selector: 'mc-syndicate-form',
  templateUrl: './syndicate-form.component.html',
  styleUrls: ['./syndicate-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SyndicateFormComponent implements OnInit {
  @Input() connector: Connector;
  @Input() licenseId: number;
  @Input() licenseUserId: number = null;

  @Output() success = new EventEmitter<boolean>();
  @Output() cancel = new EventEmitter<boolean>()

  apiKeyHide: boolean = true;

  // hide controls with buttons
  syndicateApiKeyControlHide: boolean = false;

  errorMessage: string;
  successMessage: string;
  deleteSuccessMessage: string;
  deleteConfirmDialogData: DeleteConfirmDialogData;

  syndicateForm: FormGroup<SyndicateForm>;

  constructor(private formBuilder: FormBuilder, private connectorService: ConnectorService) { }

  ngOnInit() {
    this.buildForm();
    this.setupFormData();
    // on edit switch api key with a button
    if (this.connector) {
      this.syndicateApiKeyControlHide = true;
    }
  }

  enableApiKeyControl() {
    this.syndicateApiKeyControlHide = false;
    this.syndicateForm.get('apiKey').enable();
  }

  setupFormData() {
    if (this.connector) {
      this.errorMessage = 'Unable to update connector';
      this.successMessage = 'Connector updated';
    } else {
      this.errorMessage = 'Unable to create connector';
      this.successMessage = 'Connector created';
    }
    this.deleteSuccessMessage = 'Connector deleted';
    this.deleteConfirmDialogData = { title: 'Delete Connector', prompt: `Are you sure you want to delete '${this.connector?.Name}'?` };
  }

  deleteFn = () => {
    return this.connectorService.deleteConnectors$([this.connector.Id]);
  }

  submitFn = (syndicateForm: FormGroup) => {
    let connectorUpdate: Partial<ConnectorUpdate> = {
      Id: this.connector?.Id,
      Url: syndicateForm.get('syndicateUrl').value,
      Name: syndicateForm.get('syndicateName').value,
      ConnectionType: ConnectionType.Syndicate,
      LicenseId: this.licenseId,
      LicenseUserId: this.licenseUserId
    }

    //TODO: change ClientId to ApiKey in the complier
    const settings: Partial<ConnectorSettings> = {
      Properties: {
        ElevateUrl: syndicateForm.get('elevateUrl').value,
        ClientId: syndicateForm.get('clientId').value
      } as Partial<ConnectorProperties>,
      Secrets: {} as Partial<ConnectorSecrets>
    }

    // only include secrets if the control is visible
    if (!this.syndicateApiKeyControlHide) {
      settings.Secrets.ClientSecret = syndicateForm.get('apiKey').value;
    }

    connectorUpdate.Settings = JSON.stringify(settings);

    if (this.connector) {
      return this.connectorService.updateConnector$(connectorUpdate);

    } else {
      return this.connectorService.createConnector$(connectorUpdate);
    }

  }

  buildForm() {
    let settings: ConnectorSettings;
    if (this.connector && typeof this.connector.Settings !== 'string') {
      settings = this.connector?.Settings;
    }
    this.syndicateForm = this.formBuilder.group({
      syndicateName: new FormControl(this.connector?.Name, [Validators.required, Validators.maxLength(64)]),
      elevateUrl: new FormControl(settings?.Properties?.ElevateUrl, [Validators.required, McValidators.validURLName, Validators.maxLength(256)]),
      syndicateUrl: new FormControl(this.connector?.Url, [Validators.required, McValidators.validURLName, Validators.maxLength(256)]),
      clientId: new FormControl(settings?.Properties?.ClientId, [Validators.required]),
      apiKey: new FormControl({ value: null, disabled: settings }, [Validators.required])
    });
  }

}
